/**
 * Plain-language "why the market moved" note.
 *
 * Search engines and answer engines match numbers far better when a short piece
 * of prose states the same thing the chart shows. This derives 2–3 sentences
 * straight from the live price board — direction, size, breadth, and the single
 * card doing most of the pushing — so the text can never drift from the data.
 */
import type { PriceRow } from "./prices-shared";
import { buildIndexBoard, formatChange, type IndexSeries } from "./price-index.shared";

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

function biggestMover(rows: PriceRow[], dir: 1 | -1): PriceRow | null {
  const pool = rows.filter((r) => r.market > 0 && Math.sign(r.change7d) === dir);
  if (!pool.length) return null;
  return pool.reduce((a, r) => (Math.abs(r.change7d) > Math.abs(a.change7d) ? r : a));
}

export interface MovementNote {
  /** 2–3 sentence summary, ready to render as one paragraph. */
  text: string;
  /** Direction of the 7-day move: up, down or flat. */
  direction: "up" | "down" | "flat";
}

/**
 * Builds the note for a given index series (defaults to the composite index).
 * Returns null when there is not enough history to say anything true.
 */
export function movementNote(rows: PriceRow[], series?: IndexSeries | null): MovementNote | null {
  const s = series ?? buildIndexBoard(rows).composite;
  if (!s || s.points.length < 3) return null;

  // A feed that only started a few days ago has no weekly move. Say what the
  // window actually is rather than labelling a whole-life change as a week.
  const window = s.hasWeek ? "past week" : `first ${s.spanDays + 1} days of tracking`;
  const move = s.hasWeek ? s.change7d : s.change30d;
  const direction: MovementNote["direction"] = move > 0.05 ? "up" : move < -0.05 ? "down" : "flat";
  const breadth = s.advancing + s.declining + s.unchanged;

  const verb = direction === "up" ? "rose" : direction === "down" ? "fell" : "held roughly flat";
  const level = s.level.toLocaleString("en-US", { maximumFractionDigits: 1 });
  const breadthClause = s.hasWeek
    ? `, with ${s.advancing} of ${breadth} tracked printings up and ${s.declining} down`
    : `, across ${breadth} tracked printings; per-card weekly moves need seven days of history on this marketplace and are not published yet`;
  const first =
    direction === "flat"
      ? `The index ${verb} over the ${window} at ${level}${breadthClause}.`
      : `The index ${verb} ${formatChange(move).replace("+", "")} over the ${window} to ${level}${breadthClause}.`;

  const lead = s.hasWeek ? biggestMover(rows, direction === "down" ? -1 : 1) : null;
  const second = lead
    ? `Most of the move came from ${lead.name} (${lead.code}), now ${usd(lead.market)} after a ${formatChange(lead.change7d)} week.`
    : `No single printing dominated the move, so the change reflects broad, small shifts across the basket.`;

  const third = s.hasWeek
    ? `Tracked basket value sits at ${usd(s.marketCap)}, ${formatChange(s.capChange7d)} over the same seven days.`
    : `Tracked basket value sits at ${usd(s.marketCap)} over the same window.`;

  return { text: `${first} ${second} ${third}`, direction };
}
